/** A signed-in person's own profile, password and sessions — card C-A, gates 30 and 32.
 *
 * **Every call here acts on the caller.** No hook takes a person's id, because the routes do not:
 * there is no argument that could name anyone else. That is the same property the API states, and
 * it is the reason there is no role check in this file — the question "may this person edit this
 * profile?" has one possible answer and it is not asked.
 *
 * **A read that fails throws.** `useProfile` and `useProfileSessions` return no default: a failed
 * read rendered as an empty profile or an empty list of sessions is the defect `Loaded` exists to
 * stop (*"nothing owed"*, *"every figure metered"*), and a sessions list that said *"no other
 * sessions"* because the request failed would tell a worried person the thing they most want to
 * hear and have no reason to believe.
 *
 * **A refusal is a `Refused`**, with the server's `error` code as `code`, so a form puts each
 * sentence beside the field it is about instead of in a banner. The two that matter:
 *
 * - `current_password_incorrect` is a **403**, deliberately. `client.ts` treats any 401 as *the
 *   session is over* and swaps in the sign-in form, which is the one remedy that cannot help
 *   somebody who is signed in and mistyped a field.
 * - `password_too_weak` is a **422** whose message is the rule. This exact path once returned 500,
 *   and a screen rendered it as *"we could not confirm whether you joined"*.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { instantStamp, isKnownZone, zoneName } from "../lib/format";
import type { Reader } from "../lib/format";
import { authKeys } from "./auth";
import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type Profile = components["schemas"]["ProfileOut"];
export type ProfileSession = components["schemas"]["ProfileSessionOut"];
/** One of the caller's own sign-in events (G-P8-07): when, what, why (a code or nothing), and the
 * network it came from. Never an address and never a digest. */
export type SignIn = components["schemas"]["SignInOut"];

/** What may be changed: any of three (units are not a setting - ADR-0210; the API still accepts the
 * field, and nothing here sends it). An absent key means *leave alone* — nothing here can clear a
 * value, so a field the form did not touch is not sent. */
export interface ProfileChanges {
  displayName?: string;
  locale?: "en-GB" | "en-US";
  timeZone?: string;
}

/** The closed lists, mirrored from the API's `Literal`s so a `<select>` cannot offer a value the
 * server will refuse. The generated type is the authority; these are the labels. */
export const LOCALES: ReadonlyArray<{ value: "en-GB" | "en-US"; label: string }> = [
  { value: "en-GB", label: "English (United Kingdom)" },
  { value: "en-US", label: "English (United States)" },
];

export const profileKeys = {
  profile: () => ["profile"] as const,
  sessions: () => ["profile", "sessions"] as const,
  signIns: () => ["profile", "sign-ins"] as const,
};

function isProfile(value: unknown): value is Profile {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const fields = value as Record<string, unknown>;
  return ["user_id", "display_name", "email", "role", "units", "locale", "time_zone"]
    .every((key) => typeof fields[key] === "string")
    && typeof fields.mfa_enrolled === "boolean"
    && typeof fields.email_notifications === "boolean";
}

function isSignIn(value: unknown): value is SignIn {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const fields = value as Record<string, unknown>;
  return ["id", "created_at", "event_type"].every((key) => typeof fields[key] === "string")
    && (fields.reason === null || typeof fields.reason === "string")
    && (fields.ip_prefix === null || typeof fields.ip_prefix === "string");
}

function isProfileSession(value: unknown): value is ProfileSession {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const fields = value as Record<string, unknown>;
  return ["id", "created_at", "expires_at"].every((key) => typeof fields[key] === "string")
    && typeof fields.current === "boolean"
    && (fields.last_seen_at === null || typeof fields.last_seen_at === "string")
    && (fields.ip_prefix === null || typeof fields.ip_prefix === "string");
}

async function readProfile(): Promise<Profile> {
  const { data, error, response } = await api.GET("/api/v1/profile", {});
  if (!response.ok || !isProfile(data)) {
    throw refusalFrom(response, error, "Your profile could not be read.");
  }
  return data;
}

export function useProfile() {
  return useQuery({
    queryKey: profileKeys.profile(),
    retry: false,
    queryFn: readProfile,
  });
}

/** How this person reads an instant - gate 30. Built from a profile only if it says something this
 * engine can act on; anything else is `undefined`, never a guess.
 *
 * Takes `unknown` because the answer is a network body: a stub, a proxy or a future server may send
 * a locale or a zone this build does not know, and an unrecognised value must render the instant
 * as the server sent it rather than throw inside a formatter on every screen. */
export function readerOf(profile: unknown): Reader | undefined {
  if (typeof profile !== "object" || profile === null) return undefined;
  const { locale, time_zone: timeZone } = profile as Record<string, unknown>;
  if (locale !== "en-GB" && locale !== "en-US") return undefined;
  if (typeof timeZone !== "string" || timeZone === "") return undefined;
  if (!isKnownZone(timeZone)) return undefined;
  return { locale, timeZone };
}

/** The reader's preferences as the cache holds them *right now*, for an event handler.
 *
 * **It reads and does not fetch.** `useReader` is a query, so a component that calls it asks the API
 * for the profile on mount - fine on a screen that shows the person's own time, wrong in a drawer that
 * is mounted closed on a dozen screens and only needs the locale when a button is pressed. The shell
 * has read the profile by then (`ReaderLocaleScope`), so a quantity box can ask the cache at submit
 * time and, when it is cold, fall back to the parser's default instead of starting a request. */
export function useReaderNow(): () => Reader | undefined {
  const queryClient = useQueryClient();
  return () => readerOf(queryClient.getQueryData(profileKeys.profile()));
}

/** The reader's preferences for display, or `undefined` while they are loading, failed to load or
 * are unreadable.
 *
 * **`undefined` is a state the formatters answer, not a default they fill.** It is the same shape
 * as `mayDecide` in `scope3.ts`: while the question is open the screen shows what the server said
 * (an instant in UTC, labelled UTC) and not a zone assumed on the reader's behalf. It shares the
 * profile's cache entry, so the whole app makes one request, and saving a preference on the Profile
 * screen replaces that entry and re-renders every screen reading it.
 *
 * Only call this under `RequireSession`: it asks the API, and a 401 here would swap in the sign-in
 * form. */
export function useReader(): Reader | undefined {
  const { data } = useQuery({
    queryKey: profileKeys.profile(),
    retry: false,
    // A minute, not the app default of zero: this is read by many components on one screen and a
    // preference does not change under the reader. Saving replaces the entry directly.
    staleTime: 60_000,
    queryFn: readProfile,
  });
  // `?.` on a value typed as present: the body is a network answer and a stub may send `null`.
  const locale: unknown = data?.locale;
  const timeZone: unknown = data?.time_zone;
  return useMemo(() => readerOf({ locale, time_zone: timeZone }), [locale, timeZone]);
}

/** **A saved locale redraws the screen it was saved on** (`ReaderLocaleScope` remounts its children,
 * because formatted text is not state React can see change), and a remount discards the "Saved."
 * that confirms the save. So the confirmation is carried across it: marked when a locale change is
 * saved, and taken, within two seconds, by the preferences panel that mounts next. A mark nobody
 * takes expires rather than showing up on a later visit. */
let preferencesSavedAt = 0;

export function takePreferencesSaved(): boolean {
  const fresh = Date.now() - preferencesSavedAt < 2000;
  preferencesSavedAt = 0;
  return fresh;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: ProfileChanges): Promise<Profile> => {
      const { data, error, response } = await api.PATCH("/api/v1/profile", {
        body: {
          // Spread, not `display_name: undefined`: the **absence of the key** is what says "leave
          // alone", and it should not rest on JSON dropping an undefined.
          ...(changes.displayName === undefined ? {} : { display_name: changes.displayName }),
          ...(changes.locale === undefined ? {} : { locale: changes.locale }),
          ...(changes.timeZone === undefined ? {} : { time_zone: changes.timeZone }),
        },
      });
      if (!response.ok || !isProfile(data)) {
        throw refusalFrom(response, error, "Your profile could not be saved.");
      }
      return data;
    },
    onSuccess: async (saved, changes) => {
      if (changes.locale !== undefined) preferencesSavedAt = Date.now();
      // The server's answer is what was stored, so it replaces the cached read rather than
      // waiting for one — the form must show what is on the server, not what was typed.
      queryClient.setQueryData(profileKeys.profile(), saved);
      // The name is also in the top bar, which reads `/auth/me`.
      await queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
  });
}

/** Replace the password, giving the current one.
 *
 * `gcTime: 0` so the mutation's variables — both passwords — are not kept in the query cache for
 * the default five minutes after the form has been left. */
export function useChangePassword() {
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: { currentPassword: string; newPassword: string }): Promise<void> => {
      const { error, response } = await api.POST("/api/v1/profile/password", {
        body: { current_password: input.currentPassword, new_password: input.newPassword },
      });
      if (!response.ok) {
        throw refusalFrom(response, error, "Your password could not be changed.");
      }
    },
  });
}

export function useProfileSessions() {
  return useQuery({
    queryKey: profileKeys.sessions(),
    retry: false,
    queryFn: async (): Promise<ProfileSession[]> => {
      const { data, error, response } = await api.GET("/api/v1/profile/sessions", {});
      if (!response.ok || !Array.isArray(data) || !data.every(isProfileSession)) {
        throw refusalFrom(response, error, "Your sessions could not be read.");
      }
      return data;
    },
  });
}

/** The caller's own recent sign-ins, failed attempts and lockouts, newest first (G-P8-07). **A read
 * that fails throws**, for the reason `useProfileSessions` gives: an empty list on a failed read would
 * tell a worried person that nobody has tried their account. */
export const SIGN_IN_ROWS = 10;

export function useSignIns() {
  return useQuery({
    queryKey: profileKeys.signIns(),
    retry: false,
    queryFn: async (): Promise<SignIn[]> => {
      const { data, error, response } = await api.GET("/api/v1/profile/sign-ins", {
        params: { query: { limit: SIGN_IN_ROWS } },
      });
      if (!response.ok || !Array.isArray(data) || !data.every(isSignIn)) {
        throw refusalFrom(response, error, "Your sign-in history could not be read.");
      }
      return data;
    },
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string): Promise<void> => {
      const { error, response } = await api.POST("/api/v1/profile/sessions/{session_id}/revoke", {
        params: { path: { session_id: sessionId } },
      });
      if (!response.ok) {
        throw refusalFrom(response, error, "That session could not be ended.");
      }
    },
    onSettled: async () => {
      // Settled, not only succeeded: after a refusal or a dropped response the list on screen is
      // the thing that may be wrong, and reading it again is how the person finds out.
      await queryClient.invalidateQueries({ queryKey: profileKeys.sessions() });
    },
  });
}

/** The zones the browser knows, with `UTC` first and the person's own saved value always present.
 *
 * `Intl.supportedValuesOf` is not in every engine and does not list `UTC`; a person whose stored
 * zone is missing from the list would see the select render with nothing chosen, which is the
 * period selector's bug (EXP-21) in a different control. */
export function zoneOptions(current: string): string[] {
  let known: string[];
  try {
    known = Intl.supportedValuesOf("timeZone");
  } catch {
    known = [];
  }
  return Array.from(new Set(["UTC", ...known, current])).sort((a, b) =>
    a === "UTC" ? -1 : b === "UTC" ? 1 : a.localeCompare(b),
  );
}

/** A zone as a person picks it: the city's name, what the zone is called and its offset now - **G-Wadmin-26**.
 *
 * The control listed 400 raw ids (`America/Argentina/Ushuaia`) and a person looking for Auckland had to
 * know it was `Pacific/Auckland`. The **value is still the id** (the server stores and validates ids);
 * only what is shown changes. `search` is everything a person might type for it: the city, the id (so the
 * country or region in it finds the zone), the name the platform gives it and its offset. */
export interface ZoneChoice {
  value: string;
  label: string;
  search: string;
}

function cityOf(id: string): string {
  return (id.split("/").pop() ?? id).replace(/_/g, " ");
}

let choiceCache: { at: number; byZone: Map<string, ZoneChoice> } | undefined;

/** Every zone `zoneOptions` offers, as choices, **sorted by city** with `UTC` first.
 * Built once per hour: formatting four hundred zone names is the one expensive thing here, and an
 * offset is read at one instant so the list does not change while it is open. */
export function zoneChoices(current: string, at: Date = new Date()): ZoneChoice[] {
  if (choiceCache === undefined || at.getTime() - choiceCache.at > 3_600_000) {
    choiceCache = { at: at.getTime(), byZone: new Map() };
  }
  const { byZone } = choiceCache;
  const make = (value: string): ZoneChoice => {
    const known = byZone.get(value);
    if (known) return known;
    const city = cityOf(value);
    const detail = [zoneName(value, "long", at), zoneName(value, "shortOffset", at)].filter(
      (part) => part !== "",
    );
    const choice: ZoneChoice = {
      value,
      label: detail.length > 0 ? `${city} · ${detail.join(", ")}` : city,
      search: [city, value.replace(/_/g, " "), ...detail].join(" ").toLowerCase(),
    };
    byZone.set(value, choice);
    return choice;
  };
  return zoneOptions(current)
    .map(make)
    .sort((a, b) =>
      a.value === "UTC" ? -1 : b.value === "UTC" ? 1 : cityOf(a.value).localeCompare(cityOf(b.value)) ||
        a.value.localeCompare(b.value),
    );
}

/** The choices a search leaves. **The zone already chosen always stays**, so the select never renders with
 * nothing selected (EXP-21's bug in another control); an empty query leaves everything. */
export function filterZoneChoices(
  choices: readonly ZoneChoice[],
  query: string,
  chosen: string,
): ZoneChoice[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter((word) => word !== "");
  if (words.length === 0) return [...choices];
  return choices.filter(
    (choice) => choice.value === chosen || words.every((word) => choice.search.includes(word)),
  );
}

/** An instant as a date and a time, **in the reader's zone and with the zone said**.
 *
 * Without a reader this is the UTC instant the server sent, labelled UTC - the form the session
 * list has always had. The zone is said either way, because a time with no zone is the ambiguity
 * `shortDate` exists to avoid for dates: the session list is read by someone deciding whether a
 * login was theirs. */
export function instant(iso: string | null, reader?: Reader): string {
  if (!iso) return "—";
  return instantStamp(iso, reader);
}
